/* eslint-disable relay/unused-fields */

import { graphql } from 'babel-plugin-relay/macro'
import React from 'react'
import { useFragment } from 'react-relay-offline'
import { csx } from '@vtex/admin-ui'
import { useHistory } from 'react-router'

import type { CollectionsShelfItem_IntelligentSearchProductResult$key } from './__generated__/CollectionsShelfItem_IntelligentSearchProductResult.graphql'
import {
  Product,
  ProductClickableArea,
  ProductImage,
  ProductImageUnavailable,
  ProductName,
  ProductNameUnavailable,
  ProductPrice,
  ProductPromotionHighlight,
  ProductPromotions,
} from '../../../../../../core/components'
import { useOrderForm } from '../../../../../../core/requests/checkout'
import { getUrlSearchParams } from '../../../../../../core/utils/url'
import { useFeatureFlag } from '../../../../../../core/hooks'
import { getNewProductPageUrl } from '../../../../../../core/utils/productPage'

export type CollectionsShelfItemProps = {
  connection: CollectionsShelfItem_IntelligentSearchProductResult$key
}

export const CollectionsShelfItem = ({
  connection,
}: CollectionsShelfItemProps) => {
  const history = useHistory()
  const { getCurrencyCode } = useOrderForm()
  const { isNewProductPageEnabled } = useFeatureFlag()

  const data = useFragment<CollectionsShelfItem_IntelligentSearchProductResult$key>(
    graphql`
      fragment CollectionsShelfItem_IntelligentSearchProductResult on IntelligentSearchProductResult {
        defaultImage {
          url(size: 420)
        }
        product {
          _id
          id
          name
          skus {
            edges {
              node {
                id
                images {
                  edges {
                    node {
                      id
                      url(size: 420)
                    }
                  }
                }
              }
            }
          }
          ...ProductSkuRef_Product
        }
        offers(addresses: $addresses) {
          edges {
            node {
              id
              sku {
                _id
                id
                refId
                defaultImage {
                  url(size: 420)
                }
              }
              seller {
                _id
              }
              deliveryChannel
              listPrice {
                amount
              }
              netPrice {
                amount
              }
              priority
            }
          }
          ...ProductSkuAvailabilityDelivery_ProductSkuOfferConnection
          ...ProductSkuAvailabilityPickup_ProductSkuOfferConnection
          ...ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection
        }
        promotions(addresses: $addresses) {
          edges {
            node {
              id
              name
              highlight
            }
          }
        }
      }
    `,
    connection
  )

  const offers = data.offers?.edges.map(({ node: offer }) => ({
    deliveryChannel: offer.deliveryChannel,
    sellerId: offer.seller._id,
    priority: offer.priority,
    netPrice: {
      amount: offer.netPrice.amount,
    },
    listPrice: {
      amount: offer.listPrice.amount,
    },
    productSkuId: offer.sku._id,
  }))

  const promotions = data.promotions?.edges.map((promotionEdge) => ({
    id: promotionEdge.node.id,
    name: promotionEdge.node.name,
    highlight: !!promotionEdge.node.highlight,
  }))

  const handleItemClick = () => {
    if (isNewProductPageEnabled()) {
      const skuId =
        data.offers?.edges[0]?.node.sku.id ??
        data.product.skus.edges[0]?.node.id

      const productPageUrl = getNewProductPageUrl(
        {
          skuId,
          productId: data.product.id,
          offerId: data.offers?.edges[0]?.node.id ?? '',
        },
        false
      )

      return history.push(productPageUrl)
    }

    const urlParams = getUrlSearchParams({
      skuId: offers?.[0].productSkuId ?? '',
      previousPage: 'advertisements',
    })

    history.push(`/product/${data.product._id}${urlParams}`)
  }

  const isProductAvailable = !!offers?.length

  return (
    <Product
      className={csx({
        cursor: 'pointer',
        border: '0.5rem solid transparent',
        '@desktop': {
          borderWidth: '1.25rem',
        },
      })}
      data-testid="collections-shelf-list-item"
    >
      <ProductClickableArea onClick={() => handleItemClick()}>
        {/* TODO: mover this availability logic to Product component */}
        {isProductAvailable ? (
          <>
            <ProductImage src={data.defaultImage.url} alt={data.product.name} />
            <ProductName>{data.product.name}</ProductName>
          </>
        ) : (
          <>
            <ProductImageUnavailable
              src={data.defaultImage.url}
              alt={data.product.name}
            />
            <ProductNameUnavailable>{data.product.name}</ProductNameUnavailable>
          </>
        )}
        <ProductPrice
          isBusy={false}
          currencyCode={getCurrencyCode()}
          purchaseConditionsPrices={[]}
          offers={offers}
        />
      </ProductClickableArea>
      <ProductPromotions
        promotions={promotions}
        className={csx({ marginTop: '$space-4' })}
      />
      <ProductPromotionHighlight promotions={promotions} />
    </Product>
  )
}
